// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/ManageStaff/BulkUploadModal.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../../api';
import { Download, UploadCloud, FileText, CheckCircle2, AlertTriangle, X, Loader2 } from 'lucide-react';

const BulkUploadModal = ({
  open,
  onClose,
  onSuccess,
  tenantId,
  roles = ['faculty', 'hod', 'admin', 'staff', 'teacher', 'coe', 'coe_coordinator']
}) => {
  // State management
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('idle');
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    processed: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    failed: 0
  });

  // Refs for tracking upload stats
  const uploadStatsRef = useRef({
    created: 0,
    updated: 0,
    skipped: 0,
    failed: 0
  });

  // Memoized country codes
  const countryCodes = useMemo(() => [
    { code: 'IN', prefix: '91' },
    { code: 'US', prefix: '1' },
    { code: 'GB', prefix: '44' },
    { code: 'CA', prefix: '1' },
    { code: 'AU', prefix: '61' },
    { code: 'DE', prefix: '49' },
    { code: 'FR', prefix: '33' },
    { code: 'JP', prefix: '81' },
    { code: 'CN', prefix: '86' },
    { code: 'SG', prefix: '65' },
    { code: 'MY', prefix: '60' },
    { code: 'AE', prefix: '971' },
    { code: 'SA', prefix: '966' },
    { code: 'QA', prefix: '974' },
    { code: 'KW', prefix: '965' }
  ], []);

  const getCountryPrefix = useCallback((countryCode) => {
    const country = countryCodes.find(c => c.code === countryCode);
    return country ? country.prefix : '91';
  }, [countryCodes]);

  const normalizeRoleKey = useCallback((role) => {
    if (!role) return '';
    return String(role).trim().toLowerCase().replace(/[\s-]+/g, '_');
  }, []);

  const formatRoleLabel = useCallback((role) => {
    const key = normalizeRoleKey(role);
    if (key === 'coe') return 'Controller of Examination';
    if (key === 'coe_coordinator') return 'COE Coordinator';

    return String(role || '')
      .replace(/_/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, [normalizeRoleKey]);

  const roleMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(roles) ? roles : []).forEach(role => {
      const normalized = normalizeRoleKey(role);
      if (normalized) {
        map.set(normalized, normalized);
      }
    });

    map.set('controller_of_examination', 'coe');
    map.set('coe_coordinator', 'coe_coordinator');

    return map;
  }, [roles, normalizeRoleKey]);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setIsUploading(false);
    setDownloading(false);
    setProgress(0);
    setStage('idle');
    setResults(null);
    setErrors([]);
    setDragActive(false);
    setPreviewData([]);
    setShowPreview(false);
    setUploadStats({
      total: 0, processed: 0, created: 0, updated: 0, skipped: 0, failed: 0
    });
    uploadStatsRef.current = { created: 0, updated: 0, skipped: 0, failed: 0 };
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validatePhoneNumber = useCallback((phoneNumber, countryCode = 'IN') => {
    if (!phoneNumber) return { valid: false, error: 'Phone number is required' };
    try {
      const phoneStr = phoneNumber.toString().trim();
      const digitsOnly = phoneStr.replace(/\D/g, '');
      if (!digitsOnly) return { valid: false, error: 'Phone number is required' };

      if (phoneStr.startsWith('+')) {
        if (digitsOnly.length < 8) return { valid: false, error: 'Phone number too short' };
        return { valid: true, formatted: phoneStr };
      }

      if (digitsOnly.length === 10) {
        const prefix = getCountryPrefix(countryCode);
        return { valid: true, formatted: `+${prefix}${digitsOnly}` };
      }

      if (digitsOnly.length > 10) {
        return { valid: true, formatted: `+${digitsOnly}` };
      }
      return { valid: false, error: 'Phone number must have at least 10 digits' };
    } catch (error) {
      return { valid: false, error: 'Invalid phone number format' };
    }
  }, [getCountryPrefix]);

  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') inQuotes = !inQuotes;
      else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else current += char;
    }
    values.push(current);
    return values.map(v => v.trim());
  };

  const parseCSVFile = useCallback((content) => {
    const lines = content.split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length < 2) throw new Error('File must contain header row and data.');
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/[^a-zA-Z]/g, ''));
    
    const headerMap = {
      'firstname': 'firstName', 'first': 'firstName', 'fname': 'firstName',
      'lastname': 'lastName', 'last': 'lastName', 'lname': 'lastName',
      'email': 'email', 'phonenumber': 'phoneNumber', 'phone': 'phoneNumber',
      'role': 'role', 'countrycode': 'countryCode'
    };

    return lines.slice(1).map((line, i) => {
      const values = parseCSVLine(line);
      const row = { rowNum: i + 2, firstName: '', lastName: '', email: '', phoneNumber: '', role: '', countryCode: 'IN' };
      headers.forEach((h, idx) => {
        const field = headerMap[h] || h;
        if (field in row) row[field] = values[idx];
      });
      return row;
    }).filter(r => r.firstName || r.email);
  }, []);

  const parseExcelFile = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1 });
          if (rows.length < 2) throw new Error('File is empty.');
          const headers = rows[0].map(h => h.toString().toLowerCase().replace(/[^a-zA-Z]/g, ''));
          
          const headerMap = {
            'firstname': 'firstName', 'lastname': 'lastName', 'email': 'email',
            'phonenumber': 'phoneNumber', 'role': 'role', 'countrycode': 'countryCode'
          };

          const parsed = rows.slice(1).map((row, i) => {
            const rowData = { rowNum: i + 2, firstName: '', lastName: '', email: '', phoneNumber: '', role: '', countryCode: 'IN' };
            headers.forEach((h, idx) => {
              const field = headerMap[h] || h;
              if (field in rowData) rowData[field] = row[idx]?.toString().trim() || '';
            });
            return rowData;
          }).filter(r => r.firstName || r.email);
          resolve(parsed);
        } catch (err) { reject(err); }
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const parseFile = useCallback(async (file) => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.csv')) return parseCSVFile(await file.text());
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) return parseExcelFile(file);
    throw new Error('Unsupported file type.');
  }, [parseCSVFile, parseExcelFile]);

  const validateData = useCallback((data) => {
    const validationErrors = [];
    const validData = [];
    data.forEach((row) => {
      const rowErrors = [];
      const cleanRow = { ...row };

      if (!row.email) rowErrors.push('Email is required');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim())) rowErrors.push('Invalid email');

      if (!row.firstName) rowErrors.push('First name is required');

      const phoneVal = validatePhoneNumber(row.phoneNumber, row.countryCode || 'IN');
      if (!phoneVal.valid) rowErrors.push(phoneVal.error);
      else cleanRow.phoneNumber = phoneVal.formatted;

      const normRole = normalizeRoleKey(row.role);
      if (!normRole) rowErrors.push('Role is required');
      else if (!roleMap.has(normRole)) rowErrors.push('Invalid role');
      else cleanRow.role = roleMap.get(normRole);

      if (rowErrors.length > 0) validationErrors.push({ row: row.rowNum, email: row.email, errors: rowErrors });
      else validData.push(cleanRow);
    });
    return { errors: validationErrors, validData };
  }, [roleMap, validatePhoneNumber, normalizeRoleKey]);

  const processChunk = useCallback(async (chunk, chunkIndex, totalChunks, currentStats) => {
    try {
      const response = await api.post('/api/admin/bulk-add-staff', { staffData: chunk, tenantId });
      const summary = response.data.summary || {};
      return {
        created: currentStats.created + (summary.successfullyAdded || 0),
        updated: currentStats.updated + (summary.updated || 0),
        skipped: currentStats.skipped + (summary.skipped || 0),
        failed: currentStats.failed + (response.data.results?.errors?.length || 0),
        errorDetails: response.data.results?.errors || [],
        processed: chunk.length
      };
    } catch (error) {
      return { ...currentStats, failed: currentStats.failed + chunk.length, errorDetails: [error.message], processed: chunk.length };
    }
  }, [tenantId]);

  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;
    setSelectedFile(file);
    setStage('validating');
    setProgress(20);
    try {
      const data = await parseFile(file);
      const { errors, validData } = validateData(data);
      setPreviewData(validData.slice(0, 5));
      setShowPreview(true);
      if (errors.length > 0) {
        setErrors(errors);
        setStage('error');
      } else {
        setStage('ready');
        setProgress(100);
      }
    } catch (err) {
      setErrors([{ friendlyMessage: err.message }]);
      setStage('error');
    }
  }, [parseFile, validateData]);

  const handleUpload = useCallback(async () => {
    setIsUploading(true);
    setStage('uploading');
    setProgress(0);
    const data = await parseFile(selectedFile);
    const { validData } = validateData(data);
    
    let stats = { created: 0, updated: 0, skipped: 0, failed: 0 };
    const chunkSize = 100;
    
    for (let i = 0; i < validData.length; i += chunkSize) {
      const chunk = validData.slice(i, i + chunkSize);
      const res = await processChunk(chunk, i, 0, stats);
      stats = { ...res };
      const prog = Math.round(((i + chunk.length) / validData.length) * 100);
      setProgress(prog);
      setUploadStats({ total: validData.length, processed: i + chunk.length, ...stats });
    }

    setResults({ summary: stats, errors: [], performance: { recordsPerSecond: 'N/A', duration: 'N/A' } });
    setStage('complete');
    setIsUploading(false);
    if (onSuccess) onSuccess(stats);
  }, [selectedFile, parseFile, validateData, processChunk, onSuccess]);

  const handleDownloadTemplate = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/api/admin/download-template/${tenantId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'staff_template.xlsx';
      a.click();
    } catch (err) { alert('Download failed'); }
    finally { setDownloading(false); }
  }, [tenantId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 transition-opacity duration-200">
      
      {/* Dimmed Background Overlay */}
      <div 
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity" 
        onClick={handleClose}
      />

      {/* Main Bottom Sheet Drawer (Mobile) / Center Modal Card (Desktop) */}
      <div className="relative w-full sm:max-w-xl bg-white rounded-t-2xl sm:rounded-xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[85vh] border border-slate-100 animate-slideUp sm:animate-scaleIn">
        
        {/* Mobile Handle Top Indicator Anchor */}
        <div className="w-full flex justify-center py-2 sm:hidden shrink-0">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>

        {/* Modal Window Header */}
        <div className="px-4 pt-2 pb-3 sm:py-4 flex items-center justify-between border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-base font-bold text-slate-900">Bulk Upload Staff</h2>
            <p className="text-[11px] text-slate-400 font-medium hidden sm:block">Upload Excel or CSV spreadsheet to register multiple staff roles at once.</p>
          </div>
          <button 
            type="button" 
            onClick={handleClose} 
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body Container Wrapper (Handles Scrolling Content Natively) */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-8 sm:pb-4">
          
          {/* Section Step 1: Download File Layout Sheet Template Block */}
          <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <FileText size={13} className="text-slate-500" />
                <span>Step 1: Download Template</span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium">Use our pre-formatted spreadsheet to prevent processing errors.</p>
            </div>
            <button
              onClick={handleDownloadTemplate}
              disabled={downloading || isUploading}
              className="h-9 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shrink-0 disabled:opacity-40"
            >
              {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              <span>{downloading ? 'Downloading...' : 'Get Template'}</span>
            </button>
          </div>

          {/* Section Step 2: File Interaction Drop Area Module Box */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Step 2: Upload Completed File</label>
            <div
              className={`border border-dashed rounded-xl p-6 text-center transition-all cursor-pointer flex flex-col items-center justify-center min-h-[140px] ${
                dragActive ? 'border-indigo-600 bg-indigo-50/40' : 'border-slate-200 bg-slate-50 hover:bg-slate-100/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={(e) => { e.preventDefault(); handleFileSelect(e.dataTransfer.files[0]); }}
              onClick={() => !isUploading && document.getElementById('bulkFileInput').click()}
            >
              <input 
                id="bulkFileInput" 
                type="file" 
                className="hidden" 
                accept=".csv, .xlsx, .xls"
                onChange={(e) => handleFileSelect(e.target.files[0])} 
                disabled={isUploading}
              />
              <UploadCloud size={28} className={`mb-2 ${dragActive ? 'text-indigo-600 animate-pulse' : 'text-slate-400'}`} />
              <p className="text-xs font-bold text-slate-800 max-w-[240px] truncate px-2">
                {selectedFile ? selectedFile.name : 'Choose file or drag here'}
              </p>
              <p className="text-[10px] text-slate-400 font-medium mt-0.5">Supports Excel (.xlsx) and CSV spreadsheets</p>
            </div>
          </div>

          {/* State Progress Indicators Module */}
          {stage === 'uploading' && (
            <div className="bg-slate-900 text-white rounded-xl p-3.5 space-y-2">
              <div className="flex justify-between items-center text-xs font-bold">
                <span className="flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin text-indigo-400" />
                  Processing account allocations...
                </span>
                <span className="font-mono">{progress}%</span>
              </div>
              <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div className="bg-indigo-500 h-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {/* Clean Non-Intrusive Verification Preview Table */}
          {showPreview && previewData.length > 0 && stage !== 'uploading' && stage !== 'complete' && (
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
              <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Spreadsheet Row Preview (First 5 Rows)</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-[11px] text-left divide-y divide-slate-100">
                  <thead className="bg-slate-50/50 text-slate-400 font-bold uppercase">
                    <tr>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Role</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                    {previewData.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-bold text-slate-900">{r.firstName} {r.lastName || ''}</td>
                        <td className="px-3 py-2 truncate max-w-[140px]">{r.email}</td>
                        <td className="px-3 py-2 text-slate-500">{formatRoleLabel(r.role)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Error & Validation Logs Overlay */}
          {stage === 'error' && errors.length > 0 && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3.5 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-bold text-red-700">
                <AlertTriangle size={14} className="shrink-0" />
                <span>Spreadsheet Validation Failed</span>
              </div>
              <div className="max-h-28 overflow-y-auto space-y-1 pr-1 border-t border-red-200/40 pt-2">
                {errors.map((e, i) => (
                  <p key={i} className="text-[11px] text-red-600 font-medium">
                    {e.row ? `Row ${e.row}: ` : ''}{e.errors?.join(', ') || e.friendlyMessage}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Results Scoreboard Panel View */}
          {stage === 'complete' && results && (
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
              <div className="bg-slate-900 text-white p-3 flex items-center gap-2 text-xs font-bold">
                <CheckCircle2 size={15} className="text-emerald-400" />
                <span>Batch Processing Finished</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-slate-100 border-b border-slate-100 text-center">
                <div className="p-3"><p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Total Rows</p><p className="text-base font-black text-slate-900">{uploadStats.total}</p></div>
                <div className="p-3"><p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Created</p><p className="text-base font-black text-emerald-600">{uploadStats.created}</p></div>
                <div className="p-3"><p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Skipped</p><p className="text-base font-black text-amber-600">{uploadStats.skipped}</p></div>
                <div className="p-3"><p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Failed</p><p className="text-base font-black text-rose-600">{uploadStats.failed}</p></div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Window Action Control Footer Row Panel */}
        <div className="flex flex-col-reverse sm:flex-row justify-end items-stretch sm:items-center gap-2 p-4 border-t border-slate-100 shrink-0 bg-white sm:rounded-b-xl">
          <button 
            type="button"
            onClick={handleClose} 
            disabled={isUploading}
            className="h-10 sm:h-9 px-4 text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors disabled:opacity-40"
          >
            Close
          </button>
          {stage === 'ready' && (
            <button 
              type="button"
              onClick={handleUpload} 
              disabled={isUploading} 
              className="h-10 sm:h-9 px-5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5"
            >
              {isUploading ? (
                <>
                  <Loader2 size={12} className="animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <span>Confirm & Upload</span>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;